import { DateTime } from "luxon";
import roleConfig from "../../../common/layout/roleConfig";
import { liste } from "../../ReglesSousOrientation/reglesSousOrientation";

// ============================================================
// Textes et contrôles de l'encart « Accompagnement » du dossier (CE-1370),
// purs pour être testés seuls. Les raisons viennent de l'API, telles quelles.
// ============================================================

const FUSEAU = "Indian/Reunion";
const CAF = "caf";

const jour = (iso) => DateTime.fromISO(iso).setZone(FUSEAU).toFormat("dd/MM/yyyy");

/** « du Nord », « de l'Est ». */
const duTerritoire = (territoire) => (/^[AEIOUÉ]/.test(territoire.libelle) ? `de l'${territoire.libelle}` : `du ${territoire.libelle}`);

const nom = (operateur) => (operateur ? operateur.nom : "Caf");

/** « 1 mesure restante », « 12 531 mesures restantes ». */
const mesuresRestantes = (n) => `${n.toLocaleString("fr-FR")} ${n > 1 ? "mesures restantes" : "mesure restante"}`;

const derniereCorrection = (so) => so.corrections[so.corrections.length - 1] || null;

/** À qui l'allocataire est confié aujourd'hui. */
export const nomAccompagnement = (so) => nom(so.operateur);

export function ligneAccompagnement(so) {
  const correction = derniereCorrection(so);
  if (correction) {
    return `Corrigée par ${correction.auteur.nom || "un agent"} le ${jour(correction.le)}.`;
  }
  const { decision } = so;
  if (decision.operateur && decision.regle) {
    const repartie = decision.repartition && decision.repartition.length > 1
      ? `, répartie entre ${liste(decision.repartition.map((r) => r.operateur.nom), "et")}`
      : "";
    return `Règle ${decision.regle.rang}, accompagnement total, quota ${decision.financement} ${duTerritoire(decision.territoire)} ${so.annee}${repartie}.`;
  }
  if (!so.version) {
    return "Aucune règle de sous-orientation n'était en vigueur.";
  }
  const passee = so.detail.find((d) => d.resultat === "PASSEE");
  return passee ? `Règle ${passee.rang} passée : ${passee.raison}.` : "Aucune règle ne s'applique.";
}

/** Chaque règle examinée, dans l'ordre, et la Caf quand aucune ne s'est appliquée. */
export function etapesDecision(so) {
  if (!so.version) {
    return [{ etat: "caf", texte: "Aucune version des règles n'était en vigueur : reste à la Caf" }];
  }
  const etapes = so.detail.map((d) => {
    switch (d.resultat) {
      case "APPLIQUEE":
        return { etat: "ok", texte: `Règle ${d.rang} : appliquée, vers ${nom(so.decision.operateur)}` };
      case "PASSEE":
        return { etat: "partiel", texte: `Règle ${d.rang} : passée, ${d.raison}` };
      case "NE_S_APPLIQUE_PAS":
        return { etat: "na", texte: `Règle ${d.rang} : ne s'applique pas, ${d.raison}` };
      default:
        return { etat: "avenir", texte: `Règle ${d.rang} : non examinée` };
    }
  });
  if (!so.detail.some((d) => d.resultat === "APPLIQUEE")) {
    etapes.push({ etat: "caf", texte: "Aucune règle appliquée : reste à la Caf" });
  }
  return etapes;
}

/** « 12/11/2026, Leila Gangat : FTM → Caf. Motif : « … » », de la plus ancienne à la plus récente. */
export const lignesCorrections = (so) =>
  so.corrections.map((c) => `${jour(c.le)}, ${c.auteur.nom || "un agent"} : ${nom(c.de)} → ${nom(c.vers)}. Motif : « ${c.motif} »`);

/** L'accompagnement actuel, désactivé, puis les choix que l'API propose (sans l'actuel, s'il y figurait). */
export function optionsCorrection(so) {
  const actuel = { valeur: so.operateur ? so.operateur.id : CAF, titre: nom(so.operateur), description: "Accompagnement actuel", actuel: true };
  const choix = (so.choix || []).map((c) =>
    c.operateur
      ? {
          valeur: c.operateur.id,
          titre: c.operateur.nom,
          description: `Accompagnement total · ${mesuresRestantes(c.restantes)} sur le quota ${so.decision.financement} ${duTerritoire(so.decision.territoire)} ${so.annee}`,
          actuel: false,
        }
      : { valeur: CAF, titre: "Caf", description: "Avec un travailleur social de la Caf", actuel: false }
  );
  return [actuel, ...choix.filter((c) => c.valeur !== actuel.valeur)];
}

/** Les erreurs de l'écran, sous leur champ ; les mêmes mots que l'API pour le motif. */
export function erreursCorrection({ vers, motif }) {
  const erreurs = {};
  if (!vers) erreurs.vers = "Choisissez à qui confier l'allocataire.";
  if (!(motif || "").trim()) erreurs.motif = "Indiquez le motif de la correction.";
  return erreurs;
}

export const corpsCorrection = (vers, motif) => ({ operateurId: vers === CAF ? null : vers, motif: motif.trim() });

/** Seulement pour un allocataire dont la dernière orientation est la Caf, et un rôle qui lit la sous-orientation. */
export function afficheAccompagnement(recipient, role) {
  const orientation = recipient && recipient.lastOrientation && recipient.lastOrientation.orientation;
  return orientation === "CAF" && !((role && roleConfig[role]) || []).includes("view_sous_orientation");
}
